import { Layout, Menu, Flex, Typography, Dropdown, Badge } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStoreContext } from '@/shared/lib/store-context';
import { useLogoutMutation } from '@/entities/user';
import { useOrderRequestCountQuery } from '@/entities/order-request';
import { useJoinRequestCountQuery } from '@/entities/join-request';
import { ownerPath, ROUTES } from '@/shared/config/routes';
import { WindowManagerProvider } from '@/app/providers/WindowManagerProvider';
import { HotkeyProvider } from '@/shared/lib/hotkeys';
import { useWindowManager } from '@/shared/lib/windows';
import type { ReportWindowType } from '@/shared/lib/windows';

const { Sider, Header, Content } = Layout;

const PARENT_KEYS = ['references', 'documents', 'requests', 'reports'];

/** §1: владельческий раздел — плотный сайдбар; порядок разделов внутри групп — по ТЗ, группировка — UI-группировка для краткости списка. */
export function OwnerLayout() {
  return (
    <HotkeyProvider>
      <WindowManagerProvider>
        <OwnerLayoutContent />
      </WindowManagerProvider>
    </HotkeyProvider>
  );
}

/** Внутри провайдеров — иначе клик по «Отчётам» в сайдбаре (открывает плавающее окно, не
 *  страницу) не достучится до `useWindowManager()`. */
function OwnerLayoutContent() {
  const ctx = useStoreContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const windowManager = useWindowManager();
  const { data: orderRequestCount } = useOrderRequestCountQuery(undefined, { pollingInterval: 60_000 });
  const { data: joinRequestCount } = useJoinRequestCountQuery(undefined, { pollingInterval: 60_000 });

  const referenceItems = [
    { key: ownerPath(ctx.storeId, '/categories'), label: 'Категории' },
    { key: ownerPath(ctx.storeId, '/counterparties'), label: 'Контрагенты' },
  ];

  const documentItems = [
    { key: ownerPath(ctx.storeId, '/purchases'), label: 'Приходы' },
    { key: ownerPath(ctx.storeId, '/sales'), label: 'Продажи' },
    { key: ownerPath(ctx.storeId, '/returns-in'), label: 'Возвраты от клиентов' },
    { key: ownerPath(ctx.storeId, '/returns-out'), label: 'Возвраты поставщикам' },
    { key: ownerPath(ctx.storeId, '/write-offs'), label: 'Списания' },
    { key: ownerPath(ctx.storeId, '/inventories'), label: 'Инвентаризации' },
    { key: ownerPath(ctx.storeId, '/cash-orders'), label: 'Кассовые ордера' },
  ];

  const requestItems = [
    {
      key: ownerPath(ctx.storeId, '/order-requests'),
      label: (
        <Flex align="center" gap={6}>
          Заявки на товар
          {orderRequestCount && orderRequestCount.new > 0 ? <Badge count={orderRequestCount.new} size="small" /> : null}
        </Flex>
      ),
    },
    {
      key: ownerPath(ctx.storeId, '/join-requests'),
      label: (
        <Flex align="center" gap={6}>
          Заявки на подключение
          {joinRequestCount && joinRequestCount.pending > 0 ? <Badge count={joinRequestCount.pending} size="small" /> : null}
        </Flex>
      ),
    },
  ];

  // §1, §1.8: клик открывает плавающее окно отчётов (не навигацию) — двигается, растягивается,
  // внутри само переключается между всеми 9 отчётами. `/reports/*`-маршруты остаются в роутере
  // только для прямых ссылок.
  const reportItems: { key: string; label: string; report: ReportWindowType }[] = [
    { key: ownerPath(ctx.storeId, '/reports/stock'), label: 'Остатки', report: 'stock' },
    { key: ownerPath(ctx.storeId, '/reports/turnover'), label: 'Обороты', report: 'turnover' },
    { key: ownerPath(ctx.storeId, '/reports/sales'), label: 'Продажи', report: 'sales' },
    { key: ownerPath(ctx.storeId, '/reports/profit'), label: 'Прибыль', report: 'profit' },
    { key: ownerPath(ctx.storeId, '/reports/write-offs'), label: 'Списания', report: 'write-offs' },
    { key: ownerPath(ctx.storeId, '/reports/debts'), label: 'Долги', report: 'debts' },
    { key: ownerPath(ctx.storeId, '/reports/expiry'), label: 'Сроки годности', report: 'expiry' },
    { key: ownerPath(ctx.storeId, '/reports/negative-stock'), label: 'Товары в минусе', report: 'negative-stock' },
    { key: ownerPath(ctx.storeId, '/reports/provisional-sales'), label: 'Предварительная себестоимость', report: 'provisional-sales' },
  ];
  const reportByKey = new Map(reportItems.map((r) => [r.key, r.report]));

  const requestBadgeTotal = (orderRequestCount?.new ?? 0) + (joinRequestCount?.pending ?? 0);

  const items = [
    { key: 'references', label: 'Справочники', children: referenceItems },
    { key: 'documents', label: 'Документы', children: documentItems },
    {
      key: 'requests',
      label: (
        <Flex align="center" gap={6}>
          Заявки
          {requestBadgeTotal > 0 ? <Badge count={requestBadgeTotal} size="small" /> : null}
        </Flex>
      ),
      children: requestItems,
    },
    { key: 'reports', label: 'Отчёты', children: reportItems },
    { key: 'aiChat', label: 'ИИ-ассистент' },
    { key: ownerPath(ctx.storeId, '/audit-log'), label: 'Журнал' },
    { key: ownerPath(ctx.storeId, '/settings'), label: 'Настройки' },
  ];

  const groupedLeaves = [...referenceItems, ...documentItems, ...requestItems, ...reportItems];
  const singleLeaves = items.filter((i) => !PARENT_KEYS.includes(i.key));
  const selectedKey = [...groupedLeaves, ...singleLeaves].find((i) => location.pathname.startsWith(i.key))?.key;

  const groups: [string, { key: string }[]][] = [
    ['references', referenceItems],
    ['documents', documentItems],
    ['requests', requestItems],
    ['reports', reportItems],
  ];
  const defaultOpenKeys = groups.filter(([, children]) => children.some((c) => location.pathname.startsWith(c.key))).map(([key]) => key);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={200} theme="light" style={{ borderRight: '1px solid #d9d9d9' }}>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          defaultOpenKeys={defaultOpenKeys}
          items={items}
          onClick={({ key }) => {
            if (PARENT_KEYS.includes(key)) return;
            const report = reportByKey.get(key);
            if (report) {
              windowManager.open({ kind: 'report', report });
              return;
            }
            if (key === 'aiChat') {
              windowManager.open({ kind: 'aiChat' });
              return;
            }
            void navigate(key);
          }}
          style={{ borderInlineEnd: 'none' }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', borderBottom: '1px solid #d9d9d9', padding: '0 16px', height: 40, lineHeight: '40px' }}>
          <Flex justify="space-between" align="center">
            <Typography.Text strong>{ctx.storeName}</Typography.Text>
            <Flex gap={16} align="center">
              <a onClick={() => void navigate(ROUTES.stores)}>Мои магазины</a>
              <Dropdown
                menu={{
                  items: [{ key: 'logout', label: 'Выйти', onClick: () => void logout() }],
                }}
              >
                <UserOutlined />
              </Dropdown>
            </Flex>
          </Flex>
        </Header>
        <Content style={{ padding: 12 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
